<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import type { KlaxoonEvent } from "@workadventure/shared-utils";
    import {
        ApplicationService,
        defaultNativeIntegrationAppName,
        GoogleWorkSpaceService,
        KlaxoonService,
        MediaLinkManager,
    } from "@workadventure/shared-utils";
    import CloseButton from "../../../../Components/MapEditor/PropertyEditor/CloseButton.svelte";
    import { GOOGLE_DRIVE_PICKER_APP_ID, GOOGLE_DRIVE_PICKER_CLIENT_ID } from "../../../../Enum/EnvironmentVariable";
    import LL from "../../../../../i18n/i18n-svelte";
    import type { ApplicationProperty } from "../MessageInputBar.svelte";
    import { gameManager } from "../../../../Phaser/Game/GameManager";

    const applicationManager = gameManager.getCurrentGameScene().applicationManager;

    interface Props {
        property: ApplicationProperty;
        close?: () => void;
        update?: (property: ApplicationProperty) => void;
        processing?: () => void;
        processed?: () => void;
        input?: (link: string) => void;
    }

    let {
        property,
        close = () => {},
        update = () => {},
        processing = () => {},
        processed = () => {},
        input: inputCallback = () => {},
    }: Props = $props();

    let errorLink: string | undefined = $state();
    let htmlElementInput: HTMLInputElement;
    let timeOutToFocusElement: ReturnType<typeof setTimeout>;
    let timeOutToHtmlInpuElement: ReturnType<typeof setTimeout>;

    async function initialiseLinkFactory() {
        errorLink = undefined;
        let link = property.link;

        try {
            switch (property.name) {
                case defaultNativeIntegrationAppName.KLAXOON:
                    if (applicationManager.klaxoonToolClientId == undefined) return;
                    KlaxoonService.openKlaxoonActivityPicker(
                        applicationManager.klaxoonToolClientId,
                        (payload: KlaxoonEvent) => {
                            link = KlaxoonService.getKlaxoonEmbedUrl(
                                new URL(payload.url),
                                applicationManager.klaxoonToolClientId,
                            );
                            update({ ...property, link });
                        },
                    );
                    break;
                case defaultNativeIntegrationAppName.GOOGLE_DRIVE:
                    if (GOOGLE_DRIVE_PICKER_CLIENT_ID == undefined || GOOGLE_DRIVE_PICKER_APP_ID == undefined) return;
                    link = await GoogleWorkSpaceService.initGooglePicker(
                        GOOGLE_DRIVE_PICKER_CLIENT_ID,
                        GOOGLE_DRIVE_PICKER_APP_ID,
                    );
                    break;
                case defaultNativeIntegrationAppName.GOOGLE_DOCS:
                    if (GOOGLE_DRIVE_PICKER_CLIENT_ID == undefined || GOOGLE_DRIVE_PICKER_APP_ID == undefined) return;
                    link = await GoogleWorkSpaceService.initGooglePicker(
                        GOOGLE_DRIVE_PICKER_CLIENT_ID,
                        GOOGLE_DRIVE_PICKER_APP_ID,
                        window.google.picker.ViewId.DOCS,
                    );
                    break;
                case defaultNativeIntegrationAppName.GOOGLE_SHEETS:
                    if (GOOGLE_DRIVE_PICKER_CLIENT_ID == undefined || GOOGLE_DRIVE_PICKER_APP_ID == undefined) return;
                    link = await GoogleWorkSpaceService.initGooglePicker(
                        GOOGLE_DRIVE_PICKER_CLIENT_ID,
                        GOOGLE_DRIVE_PICKER_APP_ID,
                        window.google.picker.ViewId.SPREADSHEETS,
                    );
                    break;
                case defaultNativeIntegrationAppName.GOOGLE_SLIDES:
                    if (GOOGLE_DRIVE_PICKER_CLIENT_ID == undefined || GOOGLE_DRIVE_PICKER_APP_ID == undefined) return;
                    link = await GoogleWorkSpaceService.initGooglePicker(
                        GOOGLE_DRIVE_PICKER_CLIENT_ID,
                        GOOGLE_DRIVE_PICKER_APP_ID,
                        window.google.picker.ViewId.PRESENTATIONS,
                    );
                    break;
            }
        } catch (error) {
            console.error(error);
            link = "";
        } finally {
            update({ ...property, link });
        }
    }

    async function unFocus() {
        processing();
        errorLink = undefined;
        let link = htmlElementInput.value.trim();
        try {
            let mediaLink = new MediaLinkManager(htmlElementInput.value.trim());
            mediaLink.linkMatchWithApplicationIdOrName(property.name, {
                excalidrawDomains: applicationManager.excalidrawToolDomains,
            });
            link = await mediaLink.getEmbedLink({
                klaxoonId: applicationManager.klaxoonToolClientId,
                excalidrawDomains: applicationManager.excalidrawToolDomains,
            });
            if (property.regexUrl) {
                link = ApplicationService.validateLink(
                    new URL(link),
                    property.regexUrl,
                    $LL.mapEditor.properties.openWebsite.errorEmbeddableLink(),
                    property.targetEmbedableUrl,
                );
            }
        } catch (error) {
            console.error("Error while analyzing a link:", error);
            link = "";
            errorLink = getErrorFromPropertyName() ?? errorLink ?? (error as Error).message;
        } finally {
            processed();
            update({ ...property, link });
        }
    }

    function getErrorFromPropertyName() {
        switch (property.name) {
            case defaultNativeIntegrationAppName.YOUTUBE:
                return $LL.mapEditor.properties.youtube.error();
            case defaultNativeIntegrationAppName.KLAXOON:
                return $LL.mapEditor.properties.klaxoon.error();
            case defaultNativeIntegrationAppName.GOOGLE_DRIVE:
                return $LL.mapEditor.properties.googleDrive.error();
            case defaultNativeIntegrationAppName.GOOGLE_DOCS:
                return $LL.mapEditor.properties.googleDocs.error();
            case defaultNativeIntegrationAppName.GOOGLE_SHEETS:
                return $LL.mapEditor.properties.googleSheets.error();
            case defaultNativeIntegrationAppName.GOOGLE_SLIDES:
                return $LL.mapEditor.properties.googleSlides.error();
            case defaultNativeIntegrationAppName.ERASER:
                return $LL.mapEditor.properties.eraser.error();
            case defaultNativeIntegrationAppName.EXCALIDRAW:
                return $LL.mapEditor.properties.excalidraw.error();
            case defaultNativeIntegrationAppName.CARDS:
                return $LL.mapEditor.properties.cards.error();
            case defaultNativeIntegrationAppName.TLDRAW:
                return $LL.mapEditor.properties.tldraw.error();
            default:
                return null;
        }
    }

    // Permit to broadcast update of the link after 2 seconds of inactivity and have a draft of the link
    function input() {
        errorLink = undefined;
        if (timeOutToHtmlInpuElement) clearTimeout(timeOutToHtmlInpuElement);
        timeOutToHtmlInpuElement = setTimeout(() => {
            unFocus().catch((error) => {
                console.error(error);
            });
        }, 2000);
    }

    onMount(() => {
        initialiseLinkFactory().catch((error) => {
            console.error(error);
        });
        if (timeOutToFocusElement) clearTimeout(timeOutToFocusElement);
        timeOutToFocusElement = setTimeout(() => {
            htmlElementInput.focus();
        }, 100);
    });

    onDestroy(() => {
        if (timeOutToFocusElement) clearTimeout(timeOutToFocusElement);
        if (timeOutToHtmlInpuElement) clearTimeout(timeOutToHtmlInpuElement);
    });
</script>

<div class="flex flex-col w-full justify-center items-center py-4 px-6 gap-2">
    <div class="flex flex-row w-full justify-between items-center gap-2">
        <img draggable="false" class="w-8 shrink-0" src={property.img} alt={$LL.chat.a11y.applicationIcon()} />
        <h2 class="min-w-0 flex-1 text-center text-sm p-0 m-0 leading-tight whitespace-normal wrap-break-word">
            {property.title}
        </h2>
        <CloseButton
            onclick={() => {
                close();
            }}
        />
    </div>
    <p class="text-xs text-center p-0 m-0 min-h-8 w-full leading-tight whitespace-normal wrap-break-word text-gray-400">
        {property.description}
    </p>

    <input
        data-testid="applicationInputLink"
        type="text"
        aria-label="Ссылка на приложение"
        class="border rounded w-full m-0! text-black"
        value={property.link}
        bind:this={htmlElementInput}
        oninput={() => {
            inputCallback(property.link);
            input();
        }}
        onfocusout={unFocus}
        onkeydown={(event) => {
            if (event.key === "Enter") {
                unFocus().catch((error) => {
                    console.error(error);
                });
            }
        }}
        placeholder={property.placeholder}
    />

    {#if errorLink}
        <p data-testid="applicationLinkError" class="text-xs text-red-500 p-0 m-0 h-fit w-full">
            {errorLink}
        </p>
    {/if}
</div>
